import { DatePipe } from '@angular/common';
import { HTTP_INTERCEPTORS, HttpClient, HttpClientModule } from '@angular/common/http';
import { APP_INITIALIZER, Injector, NgModule } from '@angular/core';
import { RequiredValidator } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { Router } from '@angular/router';
import { NgbDateParserFormatter } from '@ng-bootstrap/ng-bootstrap';
import { NgIdleKeepaliveModule } from '@ng-idle/keepalive';
import { OKTA_CONFIG, OktaAuthModule } from '@okta/okta-angular';
import { OktaAuth } from '@okta/okta-auth-js';
import { ToastrModule } from 'ngx-toastr';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { AppCoreModule } from './core/app-core.module';
import { FooterComponent } from './core/components/footer/footer.component';
import { GlobalModalComponent } from './core/components/global-modal/global-modal.component';
import { HeaderComponent } from './core/components/header/header.component';
import { LoadingIndicatorComponent } from './core/components/loading-indicator/loading-indicator.component';
import { NoContentComponent } from './core/components/no-content/no-content.component';
import { CustomRequiredValidatorDirective } from './core/directives/custom-required-validator.directive';
import { ApiBaseUrlInterceptor } from './core/interceptors/api-base-url.interceptor';
import { ErrorHandlingInterceptor } from './core/interceptors/error-handling.interceptor';
import { OktaTokenInterceptor } from './core/interceptors/okta-token.interceptor';
import { AgePipe } from './core/pipes/age.pipe';
import { CheckItemInArrayPipe } from './core/pipes/check-item-in-array.pipe';
import { CustomDatePipe } from './core/pipes/custom-date.pipe';
import { FilterAssessmentsByStatusPipe } from './core/pipes/filter-assessments-by-status.pipe';
import { FilterBarriersPipe } from './core/pipes/filter-barriers.pipe';
import { FilterCRAsByStatusPipe } from './core/pipes/filter-cra-by-status.pipe';
import { FilterInterventionsPipe } from './core/pipes/filter-interventions.pipe';
import { FilterListAnswersPipe } from './core/pipes/filter-list-answers.pipe';
import { FilterNotesPipe } from './core/pipes/filter-notes.pipe';
import { FilterReferralsPipe } from './core/pipes/filter-referrals.pipe';
import { FilterStrengthsPipe } from './core/pipes/filter-strengths.pipe';
import { FilterSupportsPipe } from './core/pipes/filter-supports.pipe';
import { InterventionStatusPipe } from './core/pipes/intervention-status.pipe';
import { StringToArrayPipe } from './core/pipes/string-to-array.pipe';
import { EnvironmentService } from './core/services/environment.service';
import { StartupService } from './core/services/startup.service';
import { CustomDateParserFormatter } from './core/utils/custom-date-formatter';
import { EditMyProfileModule } from './private/edit-my-profile/edit-my-profile.module';
import { EditProfileModule } from './private/edit-profile/edit-profile.module';
import { FeedbackModule } from './private/feedback/feedback.module';
import { CreateNewMessageModule } from './private/messenger/create-new-message/create-new-message.module';
import { UnreadMessageCountComponent } from './private/messenger/unread-message-count/unread-message-count.component';
import { NotificationsModule } from './private/notifications/notifications.module';
import { SideNavComponent } from './private/side-nav/side-nav.component';
import { AppNotificationComponent } from './public/login/app-notification/app-notification.component';
import { LoginSsoComponent } from './public/login/login-sso/login-sso.component';
import { LoginComponent } from './public/login/login.component';
import { LogoutComponent } from './public/logout/logout.component';

export function onStartup(startupService: StartupService): () => Promise<boolean> {
  return () => {
    return new Promise((resolve) => {
      startupService.init().subscribe(() => resolve(true));
    });
  };
}

@NgModule({
  bootstrap: [AppComponent],
  declarations: [
    AppComponent,
    SideNavComponent,
    UnreadMessageCountComponent,
    HeaderComponent,
    FooterComponent,
    GlobalModalComponent,
    LoadingIndicatorComponent,
    AppNotificationComponent,
    LoginComponent,
    LogoutComponent,
    NoContentComponent,
    LoginSsoComponent
  ],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    HttpClientModule,
    NgIdleKeepaliveModule.forRoot(),
    ToastrModule.forRoot({
      positionClass: 'toast-top-center',
      resetTimeoutOnDuplicate: true,
      progressBar: true,
      closeButton: true,
      timeOut: 5000
    }),
    AppCoreModule,
    CreateNewMessageModule,
    AppRoutingModule,
    OktaAuthModule.forRoot(),
    EditProfileModule,
    EditMyProfileModule,
    FeedbackModule,
    NotificationsModule
  ],
  providers: [
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ApiBaseUrlInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: OktaTokenInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ErrorHandlingInterceptor,
      multi: true
    },
    {
      provide: APP_INITIALIZER,
      useFactory: onStartup,
      deps: [StartupService, HttpClient, EnvironmentService],
      multi: true
    },
    CustomDatePipe,
    {
      provide: DatePipe,
      useExisting: CustomDatePipe
    },
    AgePipe,
    InterventionStatusPipe,
    FilterInterventionsPipe,
    FilterBarriersPipe,
    FilterStrengthsPipe,
    FilterSupportsPipe,
    FilterReferralsPipe,
    FilterNotesPipe,
    CheckItemInArrayPipe,
    StringToArrayPipe,
    FilterListAnswersPipe,
    FilterAssessmentsByStatusPipe,
    FilterCRAsByStatusPipe,
    {
      provide: RequiredValidator,
      useExisting: CustomRequiredValidatorDirective
    },
    {
      provide: NgbDateParserFormatter,
      useClass: CustomDateParserFormatter
    },
    {
      provide: OKTA_CONFIG,
      useFactory: (environmentService: EnvironmentService) => {
        const oktaAuth = new OktaAuth(environmentService.ssoConfig.oidc);
        return {
          oktaAuth,
          onAuthRequired: (oktaAuth: OktaAuth, injector: Injector) => {
            const router = injector.get(Router);
            router.navigate(['/login']);
          }
        };
      },
      deps: [EnvironmentService]
    }
  ]
})
export class AppModule {}
